import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Cpu, Save, Trash2, Plus, X } from "lucide-react";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ApiError } from "@/api/client";
import { useProviderRouting } from "@/api/queries/providerRouting";
import { useSetProviderRouting, useDeleteProviderRouting, useClearProviderCredential } from "@/api/mutations/providerRouting";
import {
  buildMappingFromRows,
  dropParamsWert,
  emptyRow,
  rowsFromInfo,
  smallModelOptions,
  type SlotRow,
} from "./slotList";

// #243: LLM-Provider-Routing (optionales LiteLLM-Gateway). EINE Komponente für beide Scopes:
//   - ohne agentId → Plattform-Default (gilt für alle Agents ohne eigenes Routing)
//   - mit agentId   → per-Agent-Override (gewinnt über den Plattform-Default)
// Credential ist write-only (Stand zeigt nur hasCredential, nie den Wert).
//
// #P001 (UC-1): Statt vier fester Slots (`default`/`sonnet`/`haiku`/`opus`) eine freie LISTE.
// Jeder Eintrag ist ein eigener, frei benannter Modellname im Mesh, mit eigenem An/Aus-Schalter.
// Damit gibt es auch kein Routing-weites „aktiv" mehr: „Speichern" schreibt die ganze Liste,
// „Routing löschen" wirft sie samt Gateway-Deployments weg (purge).

// #P001 (Live-Fund 2, spike112): `INPUT_CLASS` trägt `w-full` — in einer Zeile MIT mehreren
// Feldern ist das ein Klassen-Konflikt (`w-full w-40` gewinnt je nach Reihenfolge in der
// generierten CSS-Datei, nicht nach Schreibreihenfolge). Zwei `w-full`-Inputs plus feste
// Elemente in einer nowrap-Flex-Zeile liefen deshalb ~138px über die Karte hinaus — bei 1280px
// UND bei 1700px, also unabhängig von der Fensterbreite: der Aktiv-Schalter und der
// Löschen-Knopf lagen ausserhalb und waren für einen Menschen unerreichbar.
// Deshalb: EINE Basis ohne Breite, und jede Verwendung sagt ihre Breite selbst.
const INPUT_BASE =
  "flex h-10 rounded-md border border-input bg-base px-3 py-2 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-base disabled:cursor-not-allowed disabled:opacity-50";
const INPUT_CLASS = `${INPUT_BASE} w-full`;
/** Slot-NAME: feste Breite, schrumpft nicht — er ist die Kennung der Zeile. */
const NAME_INPUT_CLASS = `${INPUT_BASE} w-40 shrink-0 font-mono`;
/** Ziel-MODELL: nimmt den Rest. `min-w-0` ist Pflicht — ohne das ignoriert Flex das Schrumpfen. */
const MODEL_INPUT_CLASS = `${INPUT_BASE} min-w-0 flex-1`;

// provider = LiteLLM-Prefix. Für Ollama (nativ Anthropic-kompatibel) ist "anthropic" der schnelle
// Passthrough; "ollama_chat" ist die Übersetzungsroute. OpenAI/Gemini sprechen kein Anthropic → Übersetzung.
// ⚠ "openai" vs "hosted_vllm" ist KEINE Geschmacksfrage: LiteLLM spricht bei "openai" die
// Responses-API (/v1/responses), bei "hosted_vllm" die Chat-API (/v1/chat/completions). Viele
// OpenAI-kompatible Anbieter implementieren nur die zweite vollständig — live gemessen an Helmcode:
// über /v1/responses kommen Bilder mit 404 „No endpoints found that support image input" zurück,
// über /v1/chat/completions funktionieren sie. Text und Tools laufen auf beiden Wegen, der Verlust
// fällt also erst beim ersten Bild auf. Details: docs/07-deployment.md.
export const PROVIDERS = [
  { value: "anthropic", label: "Anthropic-kompatibel / Ollama (Passthrough)" },
  { value: "ollama_chat", label: "Ollama (Chat-Übersetzung)" },
  { value: "hosted_vllm", label: "OpenAI-kompatibler Anbieter (Chat-API) — empfohlen" },
  { value: "openai", label: "OpenAI (offizielle API, Responses)" },
  { value: "gemini", label: "Google Gemini" },
] as const;

// Hilfetext unter dem Provider-Select: die Wahl ist folgenreich und aus den Labels allein nicht
// herleitbar (zwei Einträge sehen nach „dasselbe in Grün" aus).
export const PROVIDER_HINT: Record<string, string> = {
  anthropic: "Direkter Durchreichen ohne Übersetzung — nur für Ziele, die selbst /v1/messages sprechen (z.B. Ollama Cloud).",
  ollama_chat: "Für lokale, auth-freie Ollama-Instanzen gedacht.",
  hosted_vllm: "Für OpenAI-kompatible Fremdanbieter (z.B. Helmcode, vLLM, OpenRouter). Nutzt /v1/chat/completions — nötig, damit Bilder ankommen.",
  openai: "Nur für OpenAI selbst. Nutzt /v1/responses; Fremdanbieter lehnen dort oft Bilder ab.",
  gemini: "Google Gemini über LiteLLM.",
};

// #P001: Was den Slot festhält, in der Sprache der Oberfläche. Der Master schickt nur die grobe
// Art (`kind`) plus die Id — die Übersetzung gehört hierher.
const REF_LABEL: Record<string, string> = {
  "fixed-model": "Admin-Fix",
  "allowed-models": "Whitelist",
  // #P001 (UC-3): der Kontext-Default des Agenten (Chat bzw. Prozess-Läufe).
  "model-default": "Agent-Standard",
  "process-instance": "Prozess-Instanz",
  // #P002 (UC-3): die Modell-Wahl je Schritt einer Einrichtung — eigene Art, weil sie in einem
  // ANDEREN Feld steht als das Instanz-Modell und die 409-Liste die Handlungsanweisung ist
  // („wo muss ich umhängen?").
  "process-instance-step": "Schritt-Wahl einer Einrichtung",
  "case-session": "laufender Fall",
};

interface SlotReferenceView {
  kind: string;
  id: string;
  slot: string;
  /**
   * #P002 (UC-3, Review 7 NIT-2): WO innerhalb des Ziels — bei `process-instance-step` die
   * Schritt-Id. Optional: Bestands-Arten liefern es nicht, und ein älterer Master kennt es
   * gar nicht.
   */
  detail?: string;
}

// 409 SLOT_IN_USE trägt die Referenzliste im Body (`{error, code, references}`). Alles andere
// bleibt ein normaler Fehler-Toast.
function slotInUseReferences(err: unknown): SlotReferenceView[] | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as { code?: string; references?: SlotReferenceView[] } | undefined;
  if (body?.code !== "SLOT_IN_USE") return null;
  return Array.isArray(body.references) ? body.references : [];
}

export function ProviderRoutingSection({ agentId }: { agentId?: string }) {
  const isPlatform = !agentId;
  const query = useProviderRouting(agentId);
  const setRouting = useSetProviderRouting(agentId);
  const purge = useDeleteProviderRouting(agentId);
  const clearCredential = useClearProviderCredential(agentId);
  const info = query.data;

  const [provider, setProvider] = useState<string>("anthropic");
  const [baseUrl, setBaseUrl] = useState("");
  const [credential, setCredential] = useState("");
  const [authHeader, setAuthHeader] = useState("");
  const [authScheme, setAuthScheme] = useState("");
  const [rows, setRows] = useState<SlotRow[]>([]);
  // Welche Slot-Zeile ist aufgeklappt? Der Normalfall ist Name + Modell; alles Weitere
  // (eigener Anbieter, Schlüssel, Auth-Position) sieht nur, wer es braucht.
  const [offen, setOffen] = useState<string | null>(null);
  // #P001: Referenzen aus einem 409 — sie stehen in der Sektion, nicht in einem Toast: die Liste
  // ist die Handlungsanweisung („erst das lösen, dann abschalten") und darf nicht wegblitzen.
  const [references, setReferences] = useState<SlotReferenceView[] | null>(null);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const neueZeilen = useRef(0);

  // Form aus dem Server-Stand vorbefüllen (credential bleibt leer — write-only).
  useEffect(() => {
    if (!info) return;
    if (info.provider) setProvider(info.provider);
    setBaseUrl(info.baseUrl ?? "");
    setAuthHeader(info.authHeader ?? "");
    setAuthScheme(info.authScheme ?? "");
    setRows(rowsFromInfo(info));
  }, [info]);

  const isPending = setRouting.isPending || purge.isPending || clearCredential.isPending;
  // #P001 (Review HIGH-1): „Speichern" ist ein VOLL-Ersatz — solange der Server-Stand nicht da
  // ist (lädt / Fehler), wären die Zeilen leer und der PUT löschte jeden Slot samt seinem
  // write-only-Schlüssel. Bis dahin ist jede Schreib-Aktion gesperrt; die alte `default`-Pflicht
  // hatte das nebenbei verhindert, seit 0 Slots erlaubt sind, braucht es den Riegel ausdrücklich.
  const standDa = query.isSuccess;
  const schreibGesperrt = isPending || !standDa;
  const aktiveSlots = rows.filter((r) => r.enabled && r.name.trim().length > 0).length;

  function setzen(id: string, p: Partial<SlotRow>) {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...p } : r)));
  }

  function zeileHinzufuegen() {
    neueZeilen.current += 1;
    const id = `neu-${neueZeilen.current}`;
    setRows((rs) => [...rs, emptyRow(id)]);
    setOffen(id);
  }

  function zeileEntfernen(id: string) {
    setRows((rs) => rs.filter((r) => r.id !== id));
    setOffen((o) => (o === id ? null : o));
  }

  async function save() {
    if (!standDa) return;
    const gebaut = buildMappingFromRows(rows);
    if (!gebaut.ok) {
      toast.error(gebaut.error);
      return;
    }
    setReferences(null);
    try {
      await setRouting.mutateAsync({
        provider,
        baseUrl: baseUrl.trim() || null,
        // write-only: leer = BEHALTEN (der Master fasst den gespeicherten Key nicht an).
        // Entfernen läuft über „Credential entfernen" — ein leeres Feld löscht nie.
        credential: credential,
        authHeader: authHeader.trim() || null,
        authScheme: authScheme.trim() || null,
        mapping: gebaut.mapping,
      });
      setCredential("");
      toast.success("Provider-Routing gespeichert");
    } catch (err) {
      const refs = slotInUseReferences(err);
      if (refs) {
        setReferences(refs);
        return;
      }
      toast.error(`Speichern fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  async function clearCred() {
    try {
      await clearCredential.mutateAsync();
      setCredential("");
      toast.success("Credential entfernt — das Routing bleibt bestehen");
    } catch (err) {
      toast.error(`Entfernen fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  async function routingLoeschen() {
    if (!standDa) return;
    setReferences(null);
    try {
      await purge.mutateAsync();
      setRows([]);
      setConfirmPurge(false);
      toast.success("Routing gelöscht");
    } catch (err) {
      // Auch das Löschen läuft gegen die Referenz-Sperre — dann bleibt der Dialog zu und die
      // Liste erklärt, was den Slot festhält.
      const refs = slotInUseReferences(err);
      if (refs) {
        setReferences(refs);
        setConfirmPurge(false);
        return;
      }
      toast.error(`Löschen fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  return (
    <SectionCard
      icon={<Cpu className="h-[18px] w-[18px]" />}
      title={isPlatform ? "LLM-Provider (Plattform-Default)" : "LLM-Provider (dieser Agent)"}
      description={
        isPlatform
          ? "Optionales LiteLLM-Gateway: eigene Modell-Slots statt Anthropic. Credential write-only."
          : "Optionales LiteLLM-Gateway: eigene Modell-Slots für diesen Agent. Jeder aktive Slot ist ein wählbarer Modellname. Credential write-only."
      }
    >
      <div className="space-y-4">
        {/* Status — #P001: kein Routing-weites „aktiv" mehr, der Schalter sitzt pro Slot. */}
        <div className="rounded-md border border-border bg-surface2 px-3 py-2 text-[12.5px]" data-testid="provider-routing-status">
          {query.isLoading ? (
            <span className="text-text3" data-testid="provider-routing-loading">Lade Routing…</span>
          ) : query.isError ? (
            <span className="text-danger" data-testid="provider-routing-error">
              Routing nicht abrufbar — Speichern und Löschen sind gesperrt.{" "}
              <button type="button" className="underline underline-offset-2" onClick={() => void query.refetch()}>
                Erneut laden
              </button>
            </span>
          ) : rows.length > 0 ? (
            <span className="text-text2">
              <span className="font-semibold text-text">{rows.length}</span> Slot(s), davon{" "}
              <span className="font-semibold text-text">{aktiveSlots}</span> aktiv
              {info?.provider ? <span className="text-text3"> · Vorgabe: {info.provider}</span> : null}
              {info?.configured ? <span className="text-text3"> · Gateway provisioniert</span> : null}
              {info?.hasCredential ? <span className="text-text3"> · Credential hinterlegt</span> : null}
            </span>
          ) : (
            <span className="text-text3">
              {isPlatform
                ? "Kein Plattform-Routing — Agents ohne eigene Slots laufen direkt über Anthropic."
                : "Keine eigenen Slots — dieser Agent läuft über die Anthropic-Modelle."}
            </span>
          )}
        </div>

        {/* #P001: 409 SLOT_IN_USE — was den Slot noch festhält. */}
        {references && (
          <div className="rounded-md border border-danger-border bg-danger-bg px-3 py-2 text-[12.5px]" data-testid="provider-routing-references">
            <p className="font-semibold text-text">
              Slot wird noch benutzt — abschalten oder löschen ist gesperrt.
            </p>
            {references.length === 0 ? (
              <p className="mt-1 text-text2">Der Master nennt keine Fundstelle.</p>
            ) : (
              <ul className="mt-1 space-y-0.5 text-text2">
                {references.map((r, i) => (
                  <li key={`${r.kind}-${r.id}-${r.slot}-${r.detail ?? ""}-${i}`} data-testid="provider-routing-reference">
                    <span className="font-semibold">{REF_LABEL[r.kind] ?? r.kind}</span>
                    {/* Zwei Schritte derselben Einrichtung auf demselben Slot wären ohne die
                        Schritt-Id zwei identische Zeilen — und die Liste ist die
                        Handlungsanweisung „wo muss ich umhängen?". */}
                    {r.detail ? <span className="text-text3"> · Schritt {r.detail}</span> : null}
                    {": "}
                    <span className="font-mono">{r.slot}</span>
                    {" — "}
                    <span className="font-mono text-text3">{r.id}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-1 text-[11.5px] text-text3">
              Erst die Stelle umstellen (Admin-Fix, Whitelist, Agent-Standard, Prozess-Instanz,
              laufender Fall), dann den Slot abschalten.
            </p>
          </div>
        )}

        {/* Provider */}
        <div className="space-y-1.5">
          <label className="text-[12.5px] font-medium text-text2">Provider (Vorgabe für Slots ohne eigenen)</label>
          <select className={INPUT_CLASS} value={provider} onChange={(e) => setProvider(e.target.value)} disabled={isPending} data-testid="provider-routing-provider">
            {PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          {PROVIDER_HINT[provider] && (
            <p className="text-[11.5px] leading-snug text-text3" data-testid="provider-routing-provider-hint">
              {PROVIDER_HINT[provider]}
            </p>
          )}
        </div>

        {/* baseUrl + credential */}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="text-[12.5px] font-medium text-text2">Basis-URL (optional, z.B. Ollama)</label>
            <input className={INPUT_CLASS} value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="http://host.docker.internal:11434" disabled={isPending} data-testid="provider-routing-baseurl" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[12.5px] font-medium text-text2">API-Key (write-only, leer = unverändert)</label>
            <input className={INPUT_CLASS} type="password" value={credential} onChange={(e) => setCredential(e.target.value)} placeholder={info?.hasCredential ? "●●● hinterlegt" : "leer (z.B. Ollama ohne Auth)"} disabled={isPending} data-testid="provider-routing-credential" />
            {/* #243: Entfernen ist eine EIGENE Aktion — ein leeres Feld heißt „nicht angefasst",
                sonst könnte man den Key nicht mehr von „unverändert" unterscheiden. */}
            {info?.hasCredential ? (
              <button
                type="button"
                className="text-[12px] text-text3 underline underline-offset-2 hover:text-danger disabled:opacity-50"
                onClick={clearCred}
                disabled={isPending}
                data-testid="provider-routing-clear-credential"
              >
                Credential entfernen
              </button>
            ) : null}
          </div>
        </div>

        {/* #243: Auth-Position. Leer = der Provider entscheidet (Normalfall). Nötig, wenn das
            Ziel den Schlüssel woanders liest, als LiteLLM ihn schickt — z.B. Ollama Cloud,
            das NUR `Authorization: Bearer` akzeptiert und `x-api-key` ignoriert. */}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="text-[12.5px] font-medium text-text2">Auth-Header (optional)</label>
            <input className={INPUT_CLASS} value={authHeader} onChange={(e) => setAuthHeader(e.target.value)} placeholder="leer = Provider-Standard" disabled={isPending} data-testid="provider-routing-authheader" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[12.5px] font-medium text-text2">Auth-Präfix (optional)</label>
            <input className={INPUT_CLASS} value={authScheme} onChange={(e) => setAuthScheme(e.target.value)} placeholder="z.B. Bearer" disabled={isPending} data-testid="provider-routing-authscheme" />
          </div>
        </div>
        <p className="text-[12px] text-text3">
          Nur ausfüllen, wenn der Provider den Schlüssel woanders erwartet, als der Gateway ihn
          standardmäßig sendet. Beispiel Ollama Cloud: Header <span className="font-mono">Authorization</span>,
          Präfix <span className="font-mono">Bearer</span>.
        </p>

        {/* Slot-Liste */}
        <div className="space-y-1.5">
          <label className="text-[12.5px] font-medium text-text2">Modell-Slots</label>
          <p className="text-[11.5px] leading-snug text-text3">
            Jeder Slot ist ein eigener, frei gewählter Modellname (Kleinbuchstaben, Ziffern, Punkt,
            Bindestrich — nicht <span className="font-mono">default/sonnet/haiku/opus</span> und
            nicht mit <span className="font-mono">claude</span> beginnend). Nur <b>aktive</b> Slots
            sind wählbar; jede Zeile darf ihr eigenes Ziel haben.
          </p>
          <div className="space-y-2 rounded-md border border-border bg-surface2 p-3" data-testid="provider-routing-slots">
            {rows.length === 0 && (
              <p className="text-[12px] text-text3" data-testid="provider-routing-empty">
                Noch keine Slots — mit „Slot hinzufügen" den ersten anlegen.
              </p>
            )}
            {rows.map((d) => {
              // #318: Read-out für die zugeklappte Zeile — ohne ihn wäre eine gesetzte Liste nur
              // sichtbar, wenn man die Zeile aufklappt, und niemand ahnt, dass am Ziel etwas
              // weggenommen wird.
              const drops = dropParamsWert(d.dropParams);
              // Test-/Automations-Kennung: der SLOT-Name, solange es einen gibt (er ist die
              // sprechende Kennung), sonst die Zeilen-Id einer noch namenlosen neuen Zeile.
              const tid = d.name.trim() || d.id;
              const kleineOptionen = smallModelOptions(rows, d.id);
              return (
                <div key={d.id} className="space-y-1.5">
                  {/* #P001 (Live-Fund 2): `flex-wrap` — bei schmaler Karte bricht die Zeile um,
                      statt rechts aus ihr herauszulaufen. Alles Feste trägt `shrink-0`, das
                      Modell-Feld `min-w-0 flex-1`. */}
                  <div className="flex flex-wrap items-center gap-2" data-testid={`provider-routing-row-${tid}`}>
                    <input
                      className={NAME_INPUT_CLASS}
                      value={d.name}
                      onChange={(e) => setzen(d.id, { name: e.target.value })}
                      placeholder="Slot-Name"
                      disabled={isPending}
                      data-testid={`provider-routing-name-${tid}`}
                    />
                    <input
                      className={MODEL_INPUT_CLASS}
                      value={d.model}
                      onChange={(e) => setzen(d.id, { model: e.target.value })}
                      placeholder="Zielmodell beim Anbieter, z.B. deepseek-v4-flash"
                      disabled={isPending}
                      data-testid={`provider-routing-map-${tid}`}
                    />
                    <label className="flex shrink-0 items-center gap-1 text-[11.5px] text-text3" title="Kontextfenster des Ziel-Modells in Token, z.B. 240000. Leer lassen, wenn Claude Code das Modell kennt — dann weiss es das Fenster selbst. Bei einem fremden Anbieter nimmt es sonst vorsichtshalber 200k an und verdichtet die Sitzung früher als nötig; ein zu HOHER Wert ist schlimmer: dann verdichtet es zu spät und der Anbieter weist den Turn ab.">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="w-[5.5rem] shrink-0 rounded border border-line bg-surface px-1.5 py-1 text-[11.5px] tabular-nums"
                        value={d.ctxTokens}
                        onChange={(e) => setzen(d.id, { ctxTokens: e.target.value.replace(/[^0-9]/g, "") })}
                        placeholder="Kontext"
                        disabled={isPending}
                        data-testid={`provider-routing-ctx-${tid}`}
                      />
                    </label>
                    {/* #P001: der An/Aus-Schalter DIESES Slots. Aus = bleibt konfiguriert, ist
                        aber nicht wählbar und steht nicht in der Gateway-Allowlist. */}
                    <label
                      className="flex shrink-0 items-center gap-1 text-[11.5px] text-text2"
                      title="Aktiv = dieser Name ist wählbar (/model, Whitelist, Prozesse). Aus = die Konfiguration bleibt, das Modell ist aber nicht wählbar."
                    >
                      <input
                        type="checkbox"
                        checked={d.enabled}
                        onChange={(e) => setzen(d.id, { enabled: e.target.checked })}
                        disabled={isPending}
                        data-testid={`provider-routing-enabled-${tid}`}
                      />
                      {d.enabled ? "aktiv" : "aus"}
                    </label>
                    {drops.length > 0 && (
                      <span
                        className="max-w-[9rem] shrink-0 truncate rounded border border-line bg-surface px-1.5 py-0.5 text-[11px] text-text3"
                        title={`Der Gateway entfernt diese Request-Parameter, bevor er die Anfrage an dieses Ziel schickt: ${drops.join(", ")}`}
                        data-testid={`provider-routing-dropparams-chip-${tid}`}
                      >
                        verwirft: {drops.join(", ")}
                      </span>
                    )}
                    <button
                      type="button"
                      className="shrink-0 text-[11.5px] text-accent-text underline-offset-2 hover:underline"
                      onClick={() => setOffen((o) => (o === d.id ? null : d.id))}
                      disabled={isPending}
                      data-testid={`provider-routing-toggle-${tid}`}
                    >
                      {d.eigen ? "eigener Anbieter ▾" : "Details ▸"}
                    </button>
                    <button
                      type="button"
                      className="shrink-0 rounded p-1 text-text3 hover:text-danger disabled:opacity-50"
                      onClick={() => zeileEntfernen(d.id)}
                      disabled={isPending}
                      title="Zeile entfernen (wirkt erst beim Speichern)"
                      data-testid={`provider-routing-remove-${tid}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  {/* #P002 (UC-1): was dieser Slot DARF. Bewusst in der immer sichtbaren Zeile
                      und nicht hinter „Details": es ist eine Compliance-Aussage, die man beim
                      Überfliegen der Liste sehen muss — ein Prozess mit Anforderung wählt
                      genau danach aus. */}
                  <div className="ml-2 flex flex-wrap items-center gap-3 text-[11.5px] text-text2">
                    <label className="flex items-center gap-1.5" title="Aussage des Admins, kein technischer Nachweis: Wir prüfen nicht nach, ob der Anbieter die Daten wirklich nicht speichert.">
                      <input
                        type="checkbox"
                        checked={d.sensitiveDataOk}
                        onChange={(e) => setzen(d.id, { sensitiveDataOk: e.target.checked })}
                        disabled={isPending}
                        data-testid={`provider-routing-meta-sensitive-${tid}`}
                      />
                      Für heikle Firmendaten geeignet
                    </label>
                    <label className="flex items-center gap-1.5" title="Wo läuft das Modell? Ein Prozess darf „nur lokal“ verlangen.">
                      Betrieb:
                      <select
                        className="rounded border border-line bg-surface px-1.5 py-1 text-[11.5px]"
                        value={d.hosting}
                        onChange={(e) => setzen(d.id, { hosting: e.target.value === "local" ? "local" : "cloud" })}
                        disabled={isPending}
                        data-testid={`provider-routing-meta-hosting-${tid}`}
                      >
                        <option value="cloud">Cloud</option>
                        <option value="local">lokal</option>
                      </select>
                    </label>
                    <span className="text-text3">Aussage des Admins, kein technischer Nachweis.</span>
                    {/* #P002 (Review-Runde 3, NIT-2): auf DIESEM Tab stehen zwei Sichten auf
                        denselben Slot untereinander — hier die ROHE Eingabe, in der Modell-Politik
                        darunter das Label mit der EFFEKTIVEN Aussage (Schnitt aus Slot und kleinem
                        Modell). Ohne diesen Satz sähe ein Betrachter bei einer widersprüchlichen
                        Zeile hier den Haken und darunter kein „· heikel ok", ohne zu erfahren,
                        warum. */}
                    {d.smallModel.trim() !== "" && (
                      <span className="text-text3">
                        Wirksam für die Wahl ist die strengere Aussage aus Slot und kleinem Modell.
                      </span>
                    )}
                  </div>
                  {offen === d.id && (
                    <div className="ml-2 space-y-2 rounded-md border border-border bg-base p-2.5">
                      {/* #P001: kleines Modell = Claude Codes Nebenaufgaben. Leer = der Slot
                          selbst; sonst muss es ein anderer AKTIVER Slot sein. */}
                      <div className="space-y-1">
                        <label className="text-[12px] font-medium text-text2" htmlFor={`small-${d.id}`}>
                          Kleines Modell (Nebenaufgaben)
                        </label>
                        <select
                          id={`small-${d.id}`}
                          className={INPUT_CLASS}
                          value={d.smallModel}
                          onChange={(e) => setzen(d.id, { smallModel: e.target.value })}
                          disabled={isPending}
                          data-testid={`provider-routing-smallmodel-${tid}`}
                        >
                          <option value="">— der Slot selbst —</option>
                          {kleineOptionen.map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                          {/* Ein gespeicherter Verweis auf einen inzwischen ausgeschalteten Slot
                              darf nicht still verschwinden — sonst wäre er nach dem nächsten
                              Speichern weg, ohne dass jemand ihn entfernt hat. */}
                          {d.smallModel && !kleineOptionen.includes(d.smallModel) && (
                            <option value={d.smallModel}>{d.smallModel} (nicht aktiv)</option>
                          )}
                        </select>
                        <p className="text-[11.5px] leading-snug text-text3">
                          Claude Code erledigt Kleinkram (Titel, Zusammenfassungen) mit einem
                          zweiten, günstigen Modell. Leer = derselbe Slot.
                        </p>
                      </div>
                      {/* #318: Steht bewusst VOR (und ausserhalb) des „eigener Anbieter"-Blocks —
                          der Live-Fall (bob/HF-vLLM) ist ein Slot, der seinen Anbieter von oben
                          erbt und trotzdem einen Parameter loswerden muss. Hinter dem Haken wäre
                          das Feld für genau diesen Fall unerreichbar. */}
                      <div className="space-y-1">
                        <label className="text-[12px] font-medium text-text2" htmlFor={`drop-${d.id}`}>
                          Request-Parameter verwerfen (optional)
                        </label>
                        <input
                          id={`drop-${d.id}`}
                          className={INPUT_CLASS}
                          value={d.dropParams}
                          onChange={(e) => setzen(d.id, { dropParams: e.target.value })}
                          placeholder="z.B. reasoning_effort — mehrere mit Komma trennen"
                          disabled={isPending}
                          data-testid={`provider-routing-dropparams-${tid}`}
                        />
                        <p className="text-[11.5px] leading-snug text-text3">
                          Der Gateway entfernt diese Parameter, bevor er die Anfrage an dieses Ziel
                          schickt. Nötig, wenn der Anbieter einen Parameter nicht kennt und die
                          Anfrage deshalb ablehnt — z.B. <span className="font-mono">reasoning_effort</span> an
                          einem vLLM-Endpunkt. Leer lassen, solange nichts fehlschlägt: ein Ziel, das
                          den Parameter versteht, arbeitet ohne ihn mit seiner Standard-Einstellung
                          weiter.
                        </p>
                      </div>
                      <label className="flex items-center gap-2 text-[12px] text-text2">
                        <input
                          type="checkbox"
                          checked={d.eigen}
                          onChange={(e) => setzen(d.id, { eigen: e.target.checked })}
                          disabled={isPending}
                          data-testid={`provider-routing-eigen-${tid}`}
                        />
                        Eigener Anbieter für diesen Slot (sonst gelten die Werte oben)
                      </label>
                      {d.eigen && (
                        <div className="grid gap-2 sm:grid-cols-2">
                          <select
                            className={INPUT_CLASS}
                            value={d.provider}
                            onChange={(e) => setzen(d.id, { provider: e.target.value })}
                            disabled={isPending}
                            data-testid={`provider-routing-slot-provider-${tid}`}
                          >
                            <option value="">— Anbieter wählen —</option>
                            {PROVIDERS.map((p) => (
                              <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                          </select>
                          <input
                            className={INPUT_CLASS}
                            value={d.baseUrl}
                            onChange={(e) => setzen(d.id, { baseUrl: e.target.value })}
                            placeholder="Basis-URL (leer = Anbieter-Standard)"
                            disabled={isPending}
                            data-testid={`provider-routing-slot-baseurl-${tid}`}
                          />
                          <input
                            className={INPUT_CLASS}
                            type="password"
                            value={d.credential}
                            onChange={(e) => setzen(d.id, { credential: e.target.value })}
                            placeholder={d.hasCredential ? "●●● hinterlegt (leer = unverändert)" : "API-Key für dieses Ziel"}
                            disabled={isPending}
                            data-testid={`provider-routing-slot-credential-${tid}`}
                          />
                          <div className="flex gap-2">
                            <input
                              className={INPUT_CLASS}
                              value={d.authHeader}
                              onChange={(e) => setzen(d.id, { authHeader: e.target.value })}
                              placeholder="Auth-Header (z.B. Authorization)"
                              disabled={isPending}
                              data-testid={`provider-routing-slot-authheader-${tid}`}
                            />
                            <input
                              className={INPUT_CLASS}
                              value={d.authScheme}
                              onChange={(e) => setzen(d.id, { authScheme: e.target.value })}
                              placeholder="Schema (z.B. Bearer)"
                              disabled={isPending}
                              data-testid={`provider-routing-slot-authscheme-${tid}`}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <Button variant="outline" size="sm" onClick={zeileHinzufuegen} disabled={schreibGesperrt} data-testid="provider-routing-add-slot">
              <Plus className="mr-1.5 h-4 w-4" /> Slot hinzufügen
            </Button>
          </div>
        </div>

        {/* Aktionen */}
        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={schreibGesperrt} data-testid="provider-routing-save">
            <Save className="mr-1.5 h-4 w-4" /> Speichern
          </Button>
          <Button variant="outline" onClick={() => setConfirmPurge(true)} disabled={schreibGesperrt} data-testid="provider-routing-delete">
            <Trash2 className="mr-1.5 h-4 w-4" /> Routing löschen
          </Button>
        </div>
      </div>

      {confirmPurge && (
        <ConfirmDialog
          open={confirmPurge}
          onOpenChange={setConfirmPurge}
          title="Routing löschen?"
          description="Alle Slots dieses Scopes werden entfernt und die zugehörigen Gateway-Deployments abgeräumt. Danach laufen die Turns wieder über die Anthropic-Modelle. Slots, auf die noch etwas zeigt (Admin-Fix, Whitelist, Prozess-Instanz, laufender Fall), blockieren das Löschen."
          confirmText="Routing löschen"
          confirmVariant="destructive"
          isPending={purge.isPending}
          onConfirm={routingLoeschen}
        />
      )}
    </SectionCard>
  );
}
